/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-lines */
/* eslint-env browser */

import isNil from 'lodash/isNil';
import tinycolor from 'tinycolor2';

import CommunicatorSettings from '../communicator/settings/communicator-settings';
import ErrorName from '../error/error-name';
import { defaultStretchEnum } from '../stretch/stretch-enum';

/**
 * @import { ContentInfo } from '../content/info/content-info'
 * @import { Fraction } from '../fraction'
 * @import { Medium } from '../medium/medium'
 * @import StretchEnum from '../stretch/stretch-enum'
 * @import { ViewerType } from '../viewer/type'
 * @import { Url } from '../url'
 */

/**
 * @typedef ScheduleInfoOpts
 * @property {number} [trackDuration] Délka media v tracku. // TODO: jenom kvůli PDF vieweru.
 * @property {Medium} medium Médium k přehrání.
 * @property {number} [playDuration] Délka přehrávání. [ms]
 * @property {StretchEnum} [stretchEnum] Roztažení media.
 * @property {ViewerType} [viewerType] Typ zobrazovaného media.
 * @property {Fraction} [volume] Hlasitost obsahu.
 */

/**
 * Podmnožina z ContentInfo pro účely ScheduleInfo.
 * @typedef {Pick<ContentInfo, 'communicatorSettings' | 'stretchEnum'>} ScheduleContentInfo
 */

/**
 * @type {ScheduleContentInfo}
 * @readonly
 */
export const emptyScheduleContentInfo = {
  communicatorSettings: CommunicatorSettings.empty,
  stretchEnum: defaultStretchEnum
};

/**
 * Informace potřebné k přehrání média.
 * Určené pro použití mimo plánovač.
 * Obsahuje hodnoty vypočtené v určitý okamžik.
 * Slouží pro odstínění logiky určení hodnot dle nějaké strategie.
 * Každá strategie určuje hodnoty jinak, ale vrací je v tomto jednotném formátu.
 * Obsahuje všechny informace, které může potřebovat prolínač, rodičovská strategie, analytika, ...
 */
export default class ScheduleInfo {
  /**
   * @param {ScheduleInfoOpts} deps Nepovinné parametry.
   */
  constructor(deps) {
    this.trackDuration = deps.trackDuration;
    this.medium = deps.medium;
    this.stretchEnum = deps.stretchEnum;
    this.viewerType = deps.viewerType;
    this.volume = deps.volume;
    /** @type {Url | undefined} */
    this.url = undefined;
  }

  /**
   * Zjistí barvu pozadí slajdu.
   * Hodnota odpovídá [CSS vlastnosti background-color](https://developer.mozilla.org/en-US/docs/Web/CSS/background-color).
   * @returns {string} Barva.
   */
  getBackgroundColor() {
    const { medium } = this;
    if (!isNil(medium)) {
      let color = '#000000';
      // Výchozí barva pro weby je #ffffff (bílá).
      if (
        medium.type === 'text/html' ||
        medium.type === 'application/x-kiwi-web-1'
      ) {
        color = '#ffffff';
      }

      let { backgroundColorAlpha } = medium;
      if (isNil(backgroundColorAlpha)) {
        // Webové stránky jsou ve výchozím stavu neprůhledné.
        if (
          medium.type === 'text/html' ||
          medium.type === 'application/x-kiwi-web-1'
        ) {
          backgroundColorAlpha = 1;
        } else {
          backgroundColorAlpha = 0;
        }
      }

      return tinycolor(color).setAlpha(backgroundColorAlpha).toRgbString();
    }
    // Výchozí barva pro slajdy bez šablon.
    return '#000000';
  }

  /**
   * Zjistí dobu přehrávání, po kterou má být slajd zobrazen. [ms]
   * @returns {number}
   */
  getPlayDuration() {
    // TODO Není propojeno s časovači, které se odpočítávají ve strategiích.
    // - Pouze se spoléhá na to, že implementace budou stejné.
    // - Promyslet a sjednotit určování doby přehrávání na jedno místo.
    const { trackDuration } = this;
    if (trackDuration !== undefined && Number.isFinite(trackDuration)) {
      return trackDuration;
    }
    throw new TypeError('Bad play duration.');
  }

  /**
   * Zjistí roztažení, které se má nastavit médiu.
   * @returns {StretchEnum} Roztažení.
   */
  getStretchEnum() {
    if (isNil(this.stretchEnum)) {
      throw new TypeError('Missing stretchEnum.');
    }
    return this.stretchEnum;
  }

  /**
   * Zjistí URL, která se má nastavit jako zdroj.
   * @returns {Promise<string, TypeError | (Error & { name: 'MissingMediumError' })>} URL.
   */
  async getUrl() {
    if (isNil(this.medium)) {
      throw new TypeError('Missing medium.');
    }
    const url = await this.viewerType?.getUrl(this.medium);
    if (isNil(url)) {
      const error = new Error('medium url is missing');
      error.name = ErrorName.MissingMediumError;
      throw error;
    }
    this.url = url;
    return url;
  }

  /**
   * Zjistí hlasitost, která se má nastavit médiu.
   * @returns {Fraction} Hlasitost [0-1].
   */
  getVolume() {
    const { volume } = this;
    if (volume !== undefined && Number.isFinite(volume)) {
      return volume;
    }
    return 1;
  }

  /**
   * Zjistí, jestli informace obsahují médium k přehrání.
   * @returns {boolean} Příznak.
   */
  hasMedium() {
    return !isNil(this.medium);
  }

  /**
   * Uvolní zdroje, které vznikly při volání getUrl.
   * Vrácená URL mohla být typu Blob. Pokud url nebyla Blob, tak se nic nestane.
   */
  releaseMaybeBlobUrl() {
    if (!this.url) {
      return;
    }
    // Preventivní ošetření chyby, kdyby náhodou. Podle MDN by nikdy volání
    // revokeObjectUrl nemělo vyhodit chybu. I pro non Blob URL funkce vždy uspěje.
    try {
      URL.revokeObjectURL(this.url);
    } catch {}
  }
}

/**
 * @param {Medium} medium
 * @returns {string}
 */
export function getBackgroundColor(medium) {
  let color = '#000000';
  // Výchozí barva pro weby je #ffffff (bílá).
  if (
    medium.type === 'text/html' ||
    medium.type === 'application/x-kiwi-web-1'
  ) {
    color = '#ffffff';
  }

  let { backgroundColorAlpha } = medium;
  if (isNil(backgroundColorAlpha)) {
    // Webové stránky jsou ve výchozím stavu neprůhledné.
    if (
      medium.type === 'text/html' ||
      medium.type === 'application/x-kiwi-web-1'
    ) {
      backgroundColorAlpha = 1;
    } else {
      backgroundColorAlpha = 0;
    }
  }

  return tinycolor(color).setAlpha(backgroundColorAlpha).toRgbString();
}
